import globalMethods from "@/globalMethods";
import {URLS} from "@/constants";
import {notify} from "@/plugins/notification";
import i18n from "@/i18n";


export default {
    namespaced: true,
    state: {
        appointments: [],
        isLoading: false,
        selectedShopId: null,
        selectedMasterId: null,
    },
    mutations: {
        setAppointments(state, newAppointments) {
            state.appointments = newAppointments;
        },
        addAppointment(state, appointment) {
            state.appointments.unshift(appointment);
        },
        updateAppointment(state, updatedAppointment) {
            const index = state.appointments.findIndex(a => a.id === updatedAppointment.id);
            if (index !== -1) {
                state.appointments[index] = updatedAppointment;
            }
        },
        removeAppointment(state, pk) {
            state.appointments = state.appointments.filter(a => a.id !== pk);
        },
        setLoading(state, value) {
            state.isLoading = value;
        },
        setSelectedShopId(state, value) {
            state.selectedShopId = value;
        },
        setSelectedMasterId(state, value) {
            state.selectedMasterId = value;
        },
    },
    actions: {
        async getAppointments({commit, rootState}, {shopId, masterId, dateFrom, dateTo} = {}) {
            const {personalData, company} = rootState;
            const params = new URLSearchParams();
            if (masterId) params.append("master-id", masterId);
            if (dateFrom) params.append("date-from", dateFrom);
            if (dateTo) params.append("date-to", dateTo);
            const url = `${URLS.MODEL.APPOINTMENT.LIST_URL}?${params.toString()}`;
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: shopId,
                },
                credentials: 'include',
            };
            commit("setLoading", true);
            const successfulHandler = (data) => {
                commit("setAppointments", data.data);
                return {successfully: true};
            };
            const result = await globalMethods.sendRequestToGetJson({url, options, successfulHandler});
            commit("setLoading", false);
            return result;
        },
        async createAppointment({commit, rootState}, payload) {
            const {personalData, company, cashRegister} = rootState;
            const {shopId, timezone, ...body} = payload;
            const url = URLS.MODEL.APPOINTMENT.LIST_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: shopId,
                    timezone: timezone || cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify(body),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("addAppointment", data.data);
                if (data.notification_sent && data.notification_sent.length > 0) {
                    const channels = data.notification_sent.map(c => c === "telegram" ? "Telegram" : "Max").join(", ");
                    notify(i18n.global.t("Appointment created successfully") + ". " + i18n.global.t("Notification sent via") + " " + channels, "black", 5000);
                } else if (data.warning === "master_not_scheduled") {
                    notify(i18n.global.t("Master is not scheduled to work on this day"), "orange", 5000);
                } else {
                    notify(i18n.global.t("Appointment created successfully"));
                }
                return {successfully: true};
            };
            const errorHandler = (data) => {
                if (data?.detail === "overlap") {
                    notify(i18n.global.t("This time is already booked for this master"), "red", 4000);
                } else {
                    notify(i18n.global.t("Failed to save appointment"), "red", 4000);
                }
                return {successfully: false};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler, errorHandler});
        },
        async updateAppointment({commit, rootState}, payload) {
            const {personalData, company, cashRegister} = rootState;
            const {id, pk, shopId, timezone, ...data} = payload;
            const appointmentId = id || pk;
            const url = URLS.MODEL.APPOINTMENT.DETAIL_URL(appointmentId);
            const options = {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: shopId,
                    timezone: timezone || cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify(data),
                credentials: 'include',
            };
            const successfulHandler = (responseData) => {
                commit("updateAppointment", responseData.data);
                if (responseData.warning === "no_payment_method") {
                    notify(i18n.global.t("Appointment saved, but sale was not created: no payment method"), "orange", 5000);
                } else if (responseData.warning === "transaction_limit_reached") {
                    notify(i18n.global.t("Appointment saved, but sale was not created: transaction limit reached"), "orange", 5000);
                } else if (responseData.warning === "sale_creation_failed") {
                    notify(i18n.global.t("Appointment saved, but sale creation failed"), "red", 5000);
                } else if (responseData.warning === "master_not_scheduled") {
                    notify(i18n.global.t("Master is not scheduled to work on this day"), "orange", 5000);
                } else {
                    notify(i18n.global.t("Appointment updated successfully"));
                }
                return {successfully: true};
            };
            const errorHandler = (data) => {
                if (data?.detail === "overlap") {
                    notify(i18n.global.t("This time is already booked for this master"), "red", 4000);
                } else {
                    notify(i18n.global.t("Failed to save appointment"), "red", 4000);
                }
                return {successfully: false};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler, errorHandler});
        },
        async deleteAppointment({commit, rootState}, {pk, shopId}) {
            const {personalData, company} = rootState;
            const url = URLS.MODEL.APPOINTMENT.DETAIL_URL(pk);
            const options = {
                method: "DELETE",
                headers: {
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: shopId,
                },
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("removeAppointment", pk);
                notify(i18n.global.t("Appointment deleted"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
    },
    getters: {
        appointments: (state) => state.appointments,
        isLoading: (state) => state.isLoading,
    },
};
